import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

export function useUrlFilters() {
  const router = useRouter();

  // Expose the current query params safely
  const filters = useMemo(() => {
    return router.query || {};
  }, [router.query]);

  // Read a specific query array param correctly, even if it's missing or singular string.
  const getArrayFilter = useCallback(
    (key: string, defaultValue = ['all']): string[] => {
      const value = filters[key];
      if (!value) return defaultValue;
      if (Array.isArray(value)) return value;
      return value.split(',');
    },
    [filters]
  );

  // Read a single string query param
  const getStringFilter = useCallback(
    (key: string, defaultValue = 'all'): string => {
      const value = filters[key];
      if (!value) return defaultValue;
      if (Array.isArray(value)) return value[0];
      return value;
    },
    [filters]
  );

  // Update the URL query params intelligently (merging with existing)
  // while removing keys if their value is empty.
  const setFilter = useCallback(
    (key: string, values: string[] | string | undefined | null) => {
      const newQuery = { ...router.query };

      if (
        values === undefined ||
        values === null ||
        (Array.isArray(values) && values.length === 0) ||
        (typeof values === 'string' && values === '') ||
        (typeof values === 'string' &&
          values === 'all' &&
          key !== 'networks') ||
        (Array.isArray(values) &&
          values.length === 1 &&
          values[0] === 'all' &&
          key !== 'networks') ||
        (Array.isArray(values) &&
          values.length === 1 &&
          values[0] === 'all-listings' &&
          key === 'networks')
      ) {
        // If empty or explicitly "all" default, strip it from the URL
        delete newQuery[key];
      } else {
        newQuery[key] = Array.isArray(values) ? values.join(',') : values;
      }

      router.push(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  /**
   * Reset all filter keys but maintain base routing parameters if any.
   */
  const clearAllFilters = useCallback(() => {
    router.push(
      {
        pathname: router.pathname,
        // Wipe all query parameters to cleanly reset
        query: {},
      },
      undefined,
      { shallow: true }
    );
  }, [router]);

  return {
    filters,
    getArrayFilter,
    getStringFilter,
    setFilter,
    clearAllFilters,
  };
}
